import React, { useState } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CommentIcon from '@mui/icons-material/Comment';
import ReportIcon from '@mui/icons-material/Report';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import WavesIcon from '@mui/icons-material/Waves';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { UserRole, UserType } from '../../constants/user_types';
import { ReduxState } from '../../types';
import { convertUTCToLocalSantiago } from '../../utils/formatDate';

interface NotificationItemProps {
  notification: {
    id: string;
    title: string;
    message?: string;
    createdAt: string;
    updatedAt?: string;
    type: string;
    sector?: {
      name: string;
      wiseconnZoneId: string;
      id?: string;
    } | string;
    farm?: {
      name: string;
      id: string;
    };
    createdBy?: {
      name: string;
      lastname: string;
    };
    highchartName?: string;
    is_read: boolean;
    path?: string;
    limit?: {
      name: string;
      value: number;
      top: boolean;
    };
    conversationId?: {
      _id: string;
      timePoint?: {
        day: number;
        month: number;
        year: number;
      };
    } | string;
  };
  handleMarkAsRead: (id: string) => void;
  handleMarkAsUnread: (id: string) => void;
  handleDeleteNotification: (id: string) => void;
  handleRedirectToChart: (id: string, path?: string, sectorId?: string, farmId?: string) => void;
}

// Alert configuration mapping
const ALERT_CONFIGS = {
  temperature: {
    icon: ThermostatIcon,
    colors: { high: '#B00020', low: '#3693D5' },
    titles: { high: 'Temperatura alta', low: 'Temperatura baja' },
    messages: { high: 'Temperatura ha sobrepasado los {value}°C', low: 'Temperatura es inferior a los {value}°C' },
    keywords: ['temperature', 'temperatura'],
  },
  humidity: {
    icon: OpacityIcon,
    colors: { high: '#1257A6', low: '#7797BC' },
    titles: { high: 'Humedad alta', low: 'Humedad baja' },
    messages: { high: 'Humedad ha sobrepasado los {value}%', low: 'Humedad es inferior a los {value}%' },
    keywords: ['humidity', 'humedad'],
  },
  soilMoisture: {
    icon: WavesIcon,
    colors: { high: '#307934', low: '#1E4620' },
    titles: { high: 'Humedad suelo alta', low: 'Humedad suelo baja' },
    messages: { high: 'Humedad ha sobrepasado {value}', low: 'Humedad es inferior a {value}' },
    keywords: ['soil', 'suelo', 'criticalpoint'],
  },
} as const;

// Common styles object
const STYLES = {
  icon: { fontSize: 20, color: '#6B7280' },
  smallIcon: { fontSize: 15, color: '#6B7280' },
  text: { color: '#6B7280', fontSize: '11px' },
  title: { fontWeight: 'bold', color: '#374151', ml: 1 },
  date: {
    color: '#6B7280',
    fontSize: '10px',
    display: 'block',
    mb: 0.25,
    ml: 0.5,
  },
  card: {
    backgroundColor: '#F9FAFB',
    borderRadius: 2,
    border: '1px solid #E5E7EB',
    mb: 1.5,
    position: 'relative' as const,
    overflow: 'hidden',
    '&:hover': { backgroundColor: '#F3F4F6' },
  },
  readButton: {
    position: 'absolute' as const,
    bottom: 8,
    right: 8,
    width: 20,
    height: 20,
    minWidth: 20,
    padding: 0,
    color: '#9CA3AF',
    '&:hover': {
      color: '#6B7280',
      backgroundColor: 'transparent',
    },
  },
  verticalBar: {
    position: 'absolute' as const,
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 0.5,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationContainer: {
    display: 'flex',
    alignItems: 'center',
    mb: 0.5,
  },
  messageContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    mb: 0.5,
  },
} as const;

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  handleMarkAsRead,
  handleMarkAsUnread,
  handleDeleteNotification,
  handleRedirectToChart,
}) => {
  // Get current user to check if they are admin
  const currentUser = useSelector((state: ReduxState) => state.account.user);
  const isAdmin = currentUser?.role === UserRole.ADMIN && currentUser?.type === UserType.CDTEC;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleToggleReadStatus = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (notification.is_read) {
      handleMarkAsUnread(notification.id);
    } else {
      handleMarkAsRead(notification.id);
    }
  };

  // Get alert configuration based on limit name
  const getAlertConfig = (limitName?: string) => {
    if (!limitName) return null;
    const lowerLimitName = limitName.toLowerCase();

    return Object.entries(ALERT_CONFIGS).find(([, config]) => config.keywords
      .some((keyword) => lowerLimitName.includes(keyword)))?.[1] || null;
  };

  // Get alert properties
  const getAlertProperties = () => {
    if (notification.type === 'comment') {
      return {
        icon: <CommentIcon sx={STYLES.icon} />,
        color: '#6B7280',
        title: notification.title,
        message: notification.message,
      };
    }

    const config = getAlertConfig(notification.limit?.name);
    if (!config) {
      return {
        icon: <ReportIcon sx={STYLES.icon} />,
        color: notification.limit?.top ? '#B00020' : '#3693D5',
        title: notification.title,
        message: notification.message,
      };
    }

    const isHigh = notification.limit?.top;
    const IconComponent = config.icon;

    return {
      icon: <IconComponent sx={STYLES.icon} />,
      color: isHigh ? config.colors.high : config.colors.low,
      title: isHigh ? config.titles.high : config.titles.low,
      message: (isHigh ? config.messages.high : config.messages.low)
        .replace('{value}', notification.limit?.value?.toString() || ''),
    };
  };

  const alertProps = getAlertProperties();
  const hasVerticalBar = notification.type === 'alert' || notification.type === 'comment';

  return (
    <React.Fragment>
      <Typography variant="caption" sx={STYLES.date}>
        {convertUTCToLocalSantiago(notification.createdAt)}
      </Typography>

      <Box sx={STYLES.card}>
        {hasVerticalBar && (
          <Box
            sx={{
              ...STYLES.verticalBar,
              backgroundColor: alertProps.color,
            }}
          />
        )}

        <Box sx={{ p: 1, pl: hasVerticalBar ? 2 : 1, pb: 2 }}>
          {/* Header */}
          <Box sx={STYLES.header}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Box sx={STYLES.iconContainer}>
                {alertProps.icon}
              </Box>
              <Typography variant="subtitle1" sx={STYLES.title}>
                {alertProps.title}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleOpenMenu}
              sx={{ color: '#6B7280' }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>

          {/* Location */}
          {notification.sector && (
            <Box sx={STYLES.locationContainer}>
              <Box sx={{
                ...STYLES.iconContainer,
                mr: 0.5,
                minWidth: '15px',
                pl: 0.3,
              }}
              >
                <LocationOnIcon sx={STYLES.smallIcon} />
              </Box>
              <Typography variant="body2" sx={STYLES.text}>
                {typeof notification.sector === 'string' ? notification.sector : notification.sector.name}
              </Typography>
            </Box>
          )}

          {/* Message content */}
          {notification.type === 'comment' && notification.createdBy && (
            <Box sx={{ ...STYLES.messageContainer, ml: 0.25 }}>
              <Box sx={{
                ...STYLES.iconContainer, mr: 0.5, mt: 0.1, ml: 0.25,
              }}
              >
                <AccountCircleIcon sx={STYLES.smallIcon} />
              </Box>
              <Typography variant="body2" sx={STYLES.text}>
                <strong>
                  {`${notification.createdBy.name} ${notification.createdBy.lastname}: `}
                </strong>
                {notification.message}
              </Typography>
            </Box>
          )}

          {notification.type === 'alert' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ ...STYLES.iconContainer, mr: 0.5, ml: 0.25 }}>
                {React.cloneElement(alertProps.icon, { sx: { ...STYLES.icon, fontSize: 15 } })}
              </Box>
              <Typography variant="body2" sx={STYLES.text}>
                {alertProps.message}
              </Typography>
            </Box>
          )}
        </Box>

        <IconButton
          onClick={handleToggleReadStatus}
          sx={STYLES.readButton}
          size="small"
          title={notification.is_read ? 'Marcar como no leída' : 'Marcar como leída'}
        >
          {notification.is_read ? (
            <RadioButtonCheckedIcon sx={{ fontSize: 16 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => {
            const sectorId = typeof notification.sector === 'object' ? notification.sector.id : undefined;
            const farmId = notification.farm?.id;
            handleRedirectToChart(notification.id, notification.path, sectorId, farmId);
            handleCloseMenu();
          }}>
            Ir a gráfico
          </MenuItem>
          {isAdmin && (
            <MenuItem onClick={() => {
              handleDeleteNotification(notification.id);
              handleCloseMenu();
            }}>
              Eliminar
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            handleMarkAsUnread(notification.id);
            handleCloseMenu();
          }}>
            Marcar como no leída
          </MenuItem>
        </Menu>
      </Box>
    </React.Fragment>
  );
};

export default NotificationItem;
